import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Plug, FileText, LayoutDashboard, FlaskConical } from 'lucide-react';
import { DomainIcon, KnowledgeBaseIcon } from './WorkspaceIcons';
import { canAccessPage, useCompanyCapabilities } from './CompanyCapabilities';
import { WorkspaceCompanyBrand } from './Companies';
import AccountMenu from './AccountMenu';
import { authAPI } from '../services/api';
import './WorkspaceNavigation.css';

const PanelsContext = createContext(null);
const initialCollectionPanels = { '/agent-connections': true, '/reports': true, '/dashboards': true };
export function useWorkspacePanels() {
  const shared = useContext(PanelsContext);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [knowledgeSidebarOpen, setKnowledgeSidebarOpen] = useState(true);
  const [collectionPanels, setCollectionPanels] = useState(initialCollectionPanels);
  const [terminalOpen, setTerminalOpen] = useState(false);
  return shared || { sidebarOpen, setSidebarOpen, knowledgeSidebarOpen, setKnowledgeSidebarOpen, collectionPanels, setCollectionPanels, terminalOpen, setTerminalOpen };
}
const sections = [
  ['/chat', 'Chats', MessageSquare],
  ['/domains', 'Domains', DomainIcon],
  ['/knowledge', 'Knowledge Base', KnowledgeBaseIcon],
  ['/agent-connections', 'Agent Connections', Plug],
  ['/reports', 'Reports', FileText],
  ['/evaluations', 'Evaluations', FlaskConical],
  ['/dashboards', 'Dashboards', LayoutDashboard],
];

export default function WorkspaceNavigation({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const caps = useCompanyCapabilities();
  const [user, setUser] = useState(() => authAPI.getCurrentUser?.() || null);
  useEffect(() => {
    let active = true;
    authAPI.getMe?.().then(value => { if (active) setUser(value); }).catch(() => {});
    return () => { active = false; };
  }, []);
  const isAdmin = String(user?.access_level || user?.role || '').toLowerCase() === 'admin';
  const openDialog = accountDialog => navigate('/chat', { state: { accountDialog } });
  const logout = async () => { try { await authAPI.logout(); } catch (error) { console.error('Error logging out:', error); } finally { navigate('/login'); } };
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [knowledgeSidebarOpen, setKnowledgeSidebarOpen] = useState(true);
  const [collectionPanels, setCollectionPanels] = useState(initialCollectionPanels);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [navigationExpanded, setNavigationExpanded] = useState(false);
  const navigationRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const showLabel = (event, label) => {
    if (navigationExpanded) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({ label, left: rect.right + 12, top: Math.max(24, Math.min(window.innerHeight - 24, rect.top + rect.height / 2)) });
  };
  const hideLabel = () => setTooltip(null);
  useEffect(() => {
    window.addEventListener('resize', hideLabel);
    window.addEventListener('scroll', hideLabel, true);
    return () => {
      window.removeEventListener('resize', hideLabel);
      window.removeEventListener('scroll', hideLabel, true);
    };
  }, []);
  useEffect(() => {
    if (!navigationExpanded) return;
    const collapseOutside = event => {
      if (!navigationRef.current?.contains(event.target)) setNavigationExpanded(false);
    };
    document.addEventListener('click', collapseOutside);
    return () => document.removeEventListener('click', collapseOutside);
  }, [navigationExpanded]);
  const section = sections.find(([path]) => path === pathname);
  const SectionIcon = section?.[2];
  const select = path => {
    hideLabel();
    if (path === '/chat') {
      setTerminalOpen(false);
      setSidebarOpen(open => pathname === '/chat' ? !open : true);
    }
    if (path === '/knowledge') setKnowledgeSidebarOpen(open => pathname === '/knowledge' ? !open : true);
    if (Object.prototype.hasOwnProperty.call(collectionPanels, path)) setCollectionPanels(open => ({ ...open, [path]: pathname === path ? !open[path] : true }));
    if (pathname !== path) navigate(path);
  };
  return <PanelsContext.Provider value={{ sidebarOpen, setSidebarOpen, knowledgeSidebarOpen, setKnowledgeSidebarOpen, collectionPanels, setCollectionPanels, terminalOpen, setTerminalOpen }}>
    {tooltip && !navigationExpanded && createPortal(<div className="workspace-nav-tooltip" role="tooltip" style={{ left: tooltip.left, top: tooltip.top }}>{tooltip.label}</div>, document.body)}
    <div className={`workspace-navigation-shell ${navigationExpanded ? 'navigation-expanded' : ''}`}>
      <aside ref={navigationRef} id="workspace-sidebar" className={`workspace-sidebar ${navigationExpanded ? 'expanded' : ''}`}>
        <div className="workspace-sidebar-brand"><WorkspaceCompanyBrand navigationExpanded={navigationExpanded} onToggleNavigation={() => setNavigationExpanded(expanded => !expanded)} /></div>
        <nav className="workspace-rail" aria-label="Workspace navigation">
        {sections.filter(([path]) => canAccessPage(caps, path)).map(([path, label, Icon]) => <button key={path} type="button"
          className={pathname === path ? 'active' : ''} aria-label={label} aria-current={pathname === path ? 'page' : undefined}
          aria-expanded={path === '/chat' ? pathname === '/chat' && sidebarOpen : path === '/knowledge' ? pathname === '/knowledge' && knowledgeSidebarOpen : Object.prototype.hasOwnProperty.call(collectionPanels, path) ? pathname === path && collectionPanels[path] : undefined}
          aria-controls={path === '/chat' && pathname === '/chat' ? 'chat-sidebar' : path === '/knowledge' && pathname === '/knowledge' ? 'knowledge-sidebar' : pathname === path && Object.prototype.hasOwnProperty.call(collectionPanels, path) ? `${path.slice(1)}-sidebar` : undefined}
          onMouseEnter={event => showLabel(event, label)} onMouseLeave={hideLabel}
          onFocus={event => showLabel(event, label)} onBlur={hideLabel}
          onKeyDown={event => { if (event.key === 'Escape') hideLabel(); }}
          data-prefetch={path} onClick={() => select(path)}><Icon size={21}/><span>{label}</span></button>)}
        </nav>
        <AccountMenu user={user} isAdmin={isAdmin} onOpenProfile={() => openDialog('profile')} onOpenAccount={() => navigate('/account')} onOpenUsers={() => openDialog('users')} onOpenSubscription={caps && canAccessPage(caps, '/subscription') ? () => navigate('/subscription') : undefined} onLogout={logout}/>
      </aside>
      <div className="workspace-navigation-content">
        {section && pathname !== '/chat' && <header className="workspace-section-topbar">
          <button type="button" onClick={() => select('/chat')} data-prefetch="/chat"><ArrowLeft size={17}/>Back to chat</button>
          <div className="workspace-section-brand"><SectionIcon size={20}/><span>{section[1]}</span></div>
        </header>}
        <div className="workspace-page-content">{children}</div>
      </div>
    </div>
  </PanelsContext.Provider>;
}
